import { useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client/react";
import { isEmpty, omit } from "lodash";

import { globalSearchQuery } from "src/queries/globalSearch.graphql";
import * as searchUtil from "src/util/search";
import { reportError } from "src/services/errorReporting";
import { roleBasedTagTypeIds } from "src/util/tags";
import { EVENT_TRACKING } from "src/consts/eventTracking";
import { trackEvent } from "src/services/segment";

/**
 * useGlobalSearch - Standalone hook for global search functionality
 *
 * Handles query execution, result formatting, and pagination.
 * Relies on Apollo caching for cross-component result sharing.
 *
 * @param {import("./types").GlobalSearchOptions} options
 */
export const useGlobalSearch = (options = {}) => {
  const {
    searchTerm,
    size,
    scope = null,
    peopleFilters = [],
    groupFilters = [],
    defaultTeamTypes = [],
    positionFilters = [],
    positionOccupancyStatus = null,
    tagTypesConfig = {},
    tagsVisibleIn,
    includePeople = true,
    includeGroups = true,
    includeTags = true,
    includePositions = false,
    includePositionFields = false,
    customer = null,
    disabled = false,
    allowEmptySearch = false,
  } = options;

  const client = useApolloClient();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchData, setSearchData] = useState(null);
  const [results, setResults] = useState(null);
  const [queryVars, setQueryVars] = useState(null);
  const [paginationVars, setPaginationVars] = useState(null);
  const inFlightRequestKeyRef = useRef(null);

  const executeSearch = useCallback(async () => {
    if (
      disabled ||
      (!includePeople && !includeGroups && !includeTags && !includePositions)
    ) {
      return;
    }

    const thisRequestKey = JSON.stringify(queryVars);
    inFlightRequestKeyRef.current = thisRequestKey;

    try {
      setError(null);
      setLoading(true);

      const { data } = await client.query({
        query: globalSearchQuery,
        variables: queryVars,
        fetchPolicy: "cache-first",
      });

      // Ignore response if search term/vars changed while this request was in flight
      if (inFlightRequestKeyRef.current !== thisRequestKey) {
        return;
      }

      setSearchData((prevData) => {
        // If no change in results, skip
        if (JSON.stringify(data) === JSON.stringify(prevData)) {
          return prevData;
        }

        return searchUtil.getSearchResultsMergedWithPaginatedData(
          prevData,
          data
        );
      });
    } catch (err) {
      if (inFlightRequestKeyRef.current !== thisRequestKey) {
        return;
      }
      reportError(err);
      setError(err);
    }
  }, [
    client,
    disabled,
    includeGroups,
    includePeople,
    includePositions,
    includeTags,
    queryVars,
  ]);

  // Update query vars
  useEffect(() => {
    const roleBasedTagTypes = roleBasedTagTypeIds(
      tagTypesConfig,
      tagsVisibleIn
    );

    const teamFilters = !isEmpty(scope)
      ? {
          teams: {
            teams: [scope?.id],
            includeSubTeams: true,
            intersectingTeamMembers: true,
            types: defaultTeamTypes || [],
          },
        }
      : { teams: { types: defaultTeamTypes || [] } };

    const tagFilters = includeTags
      ? { tags: { tagTypes: roleBasedTagTypes } }
      : {};

    const newVars = {
      size,
      searchTerms: searchTerm ? [searchTerm] : [],
      includePeople,
      includeGroups,
      includeTags,
      includePositions,
      includePositionFields,
      includeEmptyGroups: true,
      personFieldsToSearch: includePeople ? peopleFilters : [],
      positionFieldsToSearch: includePositions ? positionFilters : [],
      positionOccupancyStatus:
        includePositions && positionOccupancyStatus
          ? [positionOccupancyStatus]
          : null,
      groupFieldsToSearch: includeGroups ? groupFilters : [],
      tagFieldsToSearch: ["value", "type"],
      filter: {
        ...teamFilters,
        ...tagFilters,
      },
      ...paginationVars,
    };

    setQueryVars((prevVars) => {
      // If no change in query, skip
      if (JSON.stringify(newVars) === JSON.stringify(prevVars)) {
        return prevVars;
      }

      // If non-pagination vars changed, wipe state
      const omitFields = [
        "personSearchAfterId",
        "groupsOffset",
        "tagsOffset",
        "positionsPage",
      ];
      if (
        JSON.stringify(omit(newVars, omitFields)) !==
        JSON.stringify(omit(prevVars, omitFields))
      ) {
        setSearchData(null);
        setResults(null);
        setPaginationVars(null);

        // if we actually changed the pagination vars, then we will be regenerating these query vars immediately anyway, so don't return the new vars now
        if (paginationVars !== null) {
          return prevVars;
        }
      }

      return newVars;
    });
  }, [
    size,
    searchTerm,
    includePeople,
    includeGroups,
    includeTags,
    includePositions,
    includePositionFields,
    peopleFilters,
    positionFilters,
    positionOccupancyStatus,
    groupFilters,
    defaultTeamTypes,
    scope,
    tagTypesConfig,
    tagsVisibleIn,
    paginationVars,
  ]);

  // Exec search on query vars change or when disabled becomes false (e.g. navigating from search page)
  // When !allowEmptySearch, skip empty to avoid extra search before debounce settles (header search)
  useEffect(() => {
    const haveTerm = queryVars?.searchTerms?.length > 0;
    if (queryVars && !disabled && (allowEmptySearch || haveTerm)) {
      executeSearch();
    }
  }, [queryVars, disabled, executeSearch, allowEmptySearch]);

  // Updates pagination vars
  const onLoadMore = useCallback(() => {
    if (!searchData || loading) {
      return;
    }

    setPaginationVars((prevVars) => {
      const lastPersonId = searchData?.result?.pagedPeople?.searchAfterId;
      const groupCount = searchData?.result?.pagedGroups?.groups?.length;
      const tagCount = searchData?.result?.pagedTags?.tags?.length;
      const positionsCount =
        searchData?.result?.pagedPositions?.positions?.length ?? 0;
      const nextPositionsPage =
        includePositions && size > 0
          ? Math.floor(positionsCount / size) + 1
          : undefined;

      const newVars = {
        ...(includePeople && lastPersonId
          ? { personSearchAfterId: lastPersonId }
          : {}),
        ...(includeGroups && groupCount ? { groupsOffset: groupCount } : {}),
        ...(includeTags && tagCount ? { tagsOffset: tagCount } : {}),
        ...(includePositions && nextPositionsPage > 1
          ? { positionsPage: nextPositionsPage }
          : {}),
      };

      if (JSON.stringify(newVars) === JSON.stringify(prevVars)) {
        return prevVars;
      }

      return newVars;
    });
  }, [
    includeGroups,
    includePeople,
    includePositions,
    includeTags,
    loading,
    searchData,
    size,
  ]);

  // Formats search data
  useEffect(() => {
    if (disabled) {
      setLoading(false);
      setResults(null);
      setSearchData(null);
      return;
    }

    if (!searchData) {
      setResults(null);
      return;
    }

    setLoading(false);

    const formatted = searchUtil.getFormattedSearchResults({
      data: searchData,
      searchTerm,
      customer,
    });

    setResults((prev) => {
      // if same result, skip
      if (JSON.stringify(formatted) === JSON.stringify(prev)) {
        return prev;
      }

      const newGroupsCount =
        searchData?.result?.pagedGroups?.groups?.length ?? 0;
      const newTagsCount = searchData?.result?.pagedTags?.tags?.length ?? 0;
      const newPositionsCount =
        searchData?.result?.pagedPositions?.positions?.length ?? 0;
      const pageForTracking = Math.max(
        includeGroups ? Math.ceil(newGroupsCount / size) : 1,
        includeTags ? Math.ceil(newTagsCount / size) : 1,
        includePositions ? Math.ceil(newPositionsCount / size) : 1,
        1
      );
      trackEvent(EVENT_TRACKING.SEARCH_VIEWED, {
        pagination: pageForTracking,
      });

      return formatted;
    });
  }, [
    searchData,
    searchTerm,
    customer,
    size,
    includeGroups,
    includeTags,
    includePositions,
    disabled,
  ]);

  return {
    // State
    loading,
    error,

    // Results ready for rendering
    results,

    // Actions
    onLoadMore,
  };
};

export default useGlobalSearch;
